import { createApi } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query'
import { HTTP_METHODS } from 'common/constants/httpMethodsConstants'

import type { TableParamsType } from 'common/interfaces/ITable'
import { UtilService } from 'common/services/utilService'
import { baseQuery } from 'common/store/api/fetchBaseQuery'
import { type ApiPagination } from 'types'

const { withPaginationParams } = UtilService

export type IDistinctPatientMappingItem = {
  id: number
  first_name: string
  last_name: string
  full_name: string
  pharmacy_name: string
  pharmacy_phone_number: string
}

export type IDistinctPatientsResponse = {
  items: IDistinctPatientMappingItem[]
  total_items: number
  pagination: ApiPagination
}

export type IPrescriptionsItem = {
  id: number
  discharge_date: string
  facility_name: string
  patient_first_name: string
  patient_last_name: string
  patient_mrn: string | null
  encounter_id: null
  provider_uid: string
  provider_last_name: string
  provider_first_name: string
  provider_designation: string
  supervisor_last_name: string | null
  supervisor_first_name: string | null
  supervisor_designation: string | null
  medication_name: string
  medication_route_strength: string
  custom_prescription: null
  controlled_substance_schedule: null
  quantity: string
  dose: string
  frequency: string
  duration: string
  signature: string
  dispense_amount: string
  dispense_unit: string
  refills: string
  earliest_fill_date: null
  substitutes_allowed: true
  note_to_pharmacy: null
  attempted_transmission_type: string
  transmission_type: string
  e_rx_status: null
  pharmacy_name: string
  pharmacy_phone_number: string
  patient_id: null
  emr_sync_id: null
  created_at: string
  updated_at: string
}
export type IPrescriptionsResponse = {
  items: IPrescriptionsItem[]
  total_items: number
  pagination: ApiPagination
}

enum PATIENT_DATA_TAGS {
  PRESCRIPTIONS = 'Prescriptions',
  DISTINCT_PATIENTS = 'DistinctPatients',
}

export const patientDataApi = createApi({
  reducerPath: 'patientData',
  baseQuery: baseQuery(),
  tagTypes: ['PatientData'],
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    getPrescriptions: build.query<
      IPrescriptionsResponse,
      { siteId: string; tableParams: TableParamsType; with_pid: boolean; emr_sync_id: boolean }
    >({
      query: ({ siteId, tableParams, with_pid, emr_sync_id }) => {
        let url = withPaginationParams('open-emr-prescriptions', tableParams, {
          with_pid: Number(with_pid),
          emr_sync_id: Number(emr_sync_id),
        })
        return {
          url,
          headers: { 'Database-Connection': siteId },
        }
      },
      providesTags: [PATIENT_DATA_TAGS.PRESCRIPTIONS],
    }),
    getDistinctPatients: build.query<
      IDistinctPatientsResponse,
      { siteId: string; tableParams: TableParamsType }
    >({
      query: ({ siteId, tableParams }) => {
        let url = withPaginationParams('open-emr-prescriptions/distinct-patients', tableParams, {
          without_pid: 1,
        })
        return {
          url,
          headers: { 'Database-Connection': siteId },
        }
      },
      providesTags: [PATIENT_DATA_TAGS.DISTINCT_PATIENTS],
    }),
    resolvePrescription: build.mutation<
      any,
      { pid: number; first_name: string; last_name: string; siteId: string }
    >({
      query: ({ pid, first_name, last_name, siteId }) => ({
        url: 'open-emr-prescriptions/update-patient-id',
        method: 'POST',
        data: { pid, first_name, last_name },
        headers: { 'Database-Connection': siteId },
      }),
      invalidatesTags: [PATIENT_DATA_TAGS.DISTINCT_PATIENTS],
    }),
    importPrescriptions: build.mutation<any, { file: File; siteId: string }>({
      query: ({ file, siteId }) => ({
        url: 'open-emr-prescriptions/import',
        data: {
          file,
        },
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId, 'Content-Type': 'multipart/form-data' },
      }),
      invalidatesTags: [PATIENT_DATA_TAGS.DISTINCT_PATIENTS],
    }),
    exportPrescriptions: build.mutation<
      Blob,
      { siteId: string; with_pid: boolean; emr_sync_id: boolean }
    >({
      query: ({ siteId, with_pid, emr_sync_id }) => ({
        url: 'open-emr-prescriptions',
        method: HTTP_METHODS.GET,
        params: {
          with_pid: Number(with_pid),
          emr_sync_id: Number(emr_sync_id),
          export_to_file: 1,
        },
        responseType: 'arraybuffer',
        headers: {
          'Database-Connection': siteId,
          Accept: 'text/csv',
        },
      }),
    }),
  }),
})

export const {
  useLazyGetDistinctPatientsQuery,
  useGetDistinctPatientsQuery,
  useGetPrescriptionsQuery,
  useLazyGetPrescriptionsQuery,
  useResolvePrescriptionMutation,
  useImportPrescriptionsMutation,
  useExportPrescriptionsMutation,
} = patientDataApi
